import moment from 'moment-timezone'
import { scheduledTriggers } from './options'

const SECONDS_IN = {
  day: 86400,
  hour: 3600,
  min: 60,
}

const calculateTimingType = delay => {
  const timeDelay = delay ? Number(delay) : 0
  if (timeDelay >= SECONDS_IN.day) {
    return 'day'
  } else if (timeDelay >= SECONDS_IN.hour) {
    return 'hour'
  } else if (timeDelay >= SECONDS_IN.min) {
    return 'min'
  } else {
    return 'day'
  }
}

const calculateTimingValue = delay => {
  const timeDelay = delay ? Number(delay) : 0
  if (timeDelay >= SECONDS_IN.day) {
    return String(timeDelay / SECONDS_IN.day)
  } else if (timeDelay >= SECONDS_IN.hour) {
    return String(timeDelay / SECONDS_IN.hour)
  } else if (timeDelay >= SECONDS_IN.min) {
    return String(timeDelay / SECONDS_IN.min)
  } else {
    return ''
  }
}

const calculateTimingInSeconds = ({ timingType, timingValue }) => {
  return SECONDS_IN[timingType] * timingValue
}

const formatEmails = emails =>
  emails ? emails.split(',').map(email => email.trim()) : ''

const replaceTextToHtml = string =>
  string
    ? // eslint-disable-next-line no-control-regex
      `<div>${string.replace(new RegExp('\r?\n', 'g'), '<br/>')}</div>`
    : ''

const replaceHtmlToText = html =>
  html
    ? html
        // eslint-disable-next-line no-useless-escape
        .replace(/<br\s*[\/]?>/gi, '\n')
        .replace('<div>', '')
        .replace('</div>', '')
    : ''

const formatEmailTemplateData = inputsData => {
  const {
    cc: { value: cc },
    bcc: { value: bcc },
    body: { value: body },
    name: { value: name },
    sender: { value: sender },
    subject: { value: subject },
    customDynamicValues: { value: customDynamicValues },
  } = inputsData

  return {
    name,
    subject,
    sender,
    body: JSON.stringify(body),
    // body: replaceTextToHtml(body),
    cc: formatEmails(cc),
    bcc: formatEmails(bcc),
    customDynamicValues: JSON.stringify(customDynamicValues),
  }
}

const formatNotificationAndSmsData = (inputsData, commType) => {
  const {
    body: { value: body },
    name: { value: name },
    customDynamicValues: { value: customDynamicValues },
  } = inputsData

  const response = {
    body,
    name,
    customDynamicValues: JSON.stringify(customDynamicValues),
  }

  if (commType === 'notification') {
    response.title = inputsData.title.value
    response.route = inputsData.route.value
  }

  return response
}

const isScheduledTrigger = triggerName =>
  scheduledTriggers.includes(triggerName)

const calculateTimeBefore = (targetValue, timeDelay) => {
  const targetValueNumber = Number(targetValue)
  const timeDelayNumber = Number(timeDelay)
  if (
    targetValueNumber === timeDelayNumber ||
    targetValueNumber < timeDelayNumber
  ) {
    return targetValueNumber
  } else if (targetValueNumber > timeDelayNumber) {
    return targetValueNumber - timeDelayNumber
  }
}

const calculateTimeDelay = (sequence, timeBefore, oldTimeDelay) => {
  if (sequence.hasScheduledTrigger) {
    const higherTimeDelay = sequence.Action.reduce((total, action) => {
      if (Number(action.time_before) > total) {
        total = Number(action.time_before)
      }
      return total
    }, 0)
    return higherTimeDelay - Number(timeBefore)
  } else {
    return oldTimeDelay || ''
  }
}

const calculateTargetValue = (sequence, oldTargetValue) => {
  if (sequence.hasScheduledTrigger) {
    return sequence.Action.reduce((total, action) => {
      if (Number(action.time_before) > total) {
        total = Number(action.time_before)
      }
      return total
    }, 0)
  } else {
    return oldTargetValue || ''
  }
}

const filterAndSortSequences = (
  sequences,
  communicationsType,
  sequencesType,
  sequencesSource,
  resultPerPage,
  page,
) => {
  const filteredAndSortedSequences = sequences
    .filter(sequence => {
      if (communicationsType !== 'all') {
        const communication = sequence.params.actionsRaw.find(action => {
          return action.actionType.name.toLowerCase() === communicationsType
        })
        return communication ? true : false
      } else {
        return sequence
      }
    })
    .filter(sequence => {
      if (sequencesSource !== 'all') {
        if (sequencesSource === 'editable') {
          return sequence.params.editable
        } else if (sequencesSource === 'hardcoded') {
          return !sequence.params.editable
        }
        return sequence
      } else {
        return sequence
      }
    })
    .filter(sequence => {
      if (sequencesType !== 'all') {
        if (sequencesType === 'schedule') {
          return sequence.params.scheduleDriven
        } else if (sequencesType === 'trigger') {
          return sequence.params.triggerDriven
        }
        return false
      } else {
        return true
      }
    })
    .reduce(
      (acc, curr) => {
        if (acc[curr.params.stageRaw]) {
          acc[curr.params.stageRaw].push(curr)
        } else {
          acc.noStage.push(curr)
        }
        return acc
      },
      {
        'interested': [],
        'booked': [],
        'videoOnboarding': [],
        'test': [],
        'preAccess': [],
        'finalReview': [],
        'approved': [],
        'uninterested': [],
        'refused': [],
        'noStage': [],
      },
    )

  const rows = Object.values(filteredAndSortedSequences).reduce((acc, curr) => {
    acc.push(...curr.reverse())
    return acc
  }, [])

  return {
    totalRows: rows.length,
    rows: rows.splice(page * resultPerPage, resultPerPage),
  }
}

const actionTypesByName = {
  'email': '1',
  'sms': '2',
  'notification': '3',
}

/**
 * @param {Object} params
 * @param {(x: object) => void} params.setConfirmDialogData
 * @param {string} params.title
 * @param {boolean} [params.hasOptions]
 * @param {function} [params.onConfirm]
 * @param {string} [params.rightButtonLable]
 * @param {string} [params.leftButtonLable]
 * @param {string} [params.contentText]
 */
const handleConfirmationDialog = ({
  setConfirmDialogData,
  title,
  hasOptions,
  onConfirm,
  rightButtonLable,
  leftButtonLable,
  contentText,
}) => {
  return new Promise((res, rej) => {
    setConfirmDialogData({
      isOpen: true,
      title,
      onAgree: res,
      onDisagree: hasOptions ? rej : undefined,
      rightButtonLable,
      leftButtonLable,
      contentText: contentText || '',
    })
  })
    .then(() => {
      setConfirmDialogData({ isOpen: false, title })
      if (onConfirm) onConfirm()
    })
    .catch(() => {
      setConfirmDialogData({ isOpen: false })
    })
}

const castToMoment = ({ year, month, day, hour, minute, option = 'AM' }) => {
  let dateHour = hour
  if (option === 'AM') {
    dateHour = Number(hour) === 12 ? '00' : hour
  } else if (option === 'PM') {
    dateHour = Number(hour) === 12 ? hour : Number(hour) + 12
  }
  const date = moment(
    `${year}-${month}-${day} ${
      `${dateHour}`.length === 1 ? `0${dateHour}` : dateHour
    }:${minute}`,
    'YYYY-MM-DD HH:mm',
  )

  return date
}

const castMomentToTime = date => {
  if (!date) {
    return {
      hour: '00',
      minute: '00',
      option: 'AM',
    }
  }

  const hour = date.format('HH')
  const minute = date.format('mm')
  const option = Number(hour) >= 12 ? 'PM' : 'AM'
  let hour12 = Number(hour) > 12 ? Number(hour) - 12 : hour
  if (hour12 === '00') {
    hour12 = '12'
  }
  return {
    hour: `${hour12}`.length === 1 ? `0${hour12}` : hour12,
    minute,
    option,
  }
}

export {
  formatEmailTemplateData,
  formatNotificationAndSmsData,
  castToMoment,
  castMomentToTime,
  replaceTextToHtml,
  actionTypesByName,
  replaceHtmlToText,
  isScheduledTrigger,
  calculateTimeBefore,
  calculateTimeDelay,
  calculateTargetValue,
  handleConfirmationDialog,
  calculateTimingType,
  calculateTimingValue,
  calculateTimingInSeconds,
  filterAndSortSequences,
}
